import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../messages/ThreadQuill.css';
import {
  addTaskNote,
  deleteTaskNote,
  markTaskNotesRead,
  updateTaskNote,
} from './store/checklistSlice';

const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const quillFormats = ['bold', 'italic', 'underline', 'list', 'bullet', 'link'];

const isQuillEmpty = val => !val || val.replace(/<[^>]*>/g, '').trim() === '';

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('pl-PL', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const TaskNotesPanel = ({ task: initialTask, playerId, onClose }) => {
  const dispatch = useDispatch();
  const currentUserEmail = useSelector(({ authorization }) => authorization.user?.universityEmail || authorization.user?.personalEmail);
  const userRole = useSelector(({ authorization }) => authorization.userRole);
  const [task, setTask] = useState(initialTask);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const notesContainerRef = useRef(null);
  const bottomRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);

  useEffect(() => {
    setTask(initialTask);
  }, [initialTask]);

  // Mark internal notes as read when panel opens
  useEffect(() => {
    if (playerId && initialTask._id) {
      dispatch(markTaskNotesRead(playerId, initialTask._id));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const notesCount = (task.internalNotes || []).length;

  useEffect(() => {
    if (shouldAutoScrollRef.current && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [notesCount]);

  const handleNotesScroll = () => {
    const el = notesContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    shouldAutoScrollRef.current = distanceFromBottom < 80;
  };

  const sortedNotes = [...(task.internalNotes || [])].sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
  );

  const handleSubmit = async () => {
    if (isQuillEmpty(text) || submitting) return;
    setSubmitting(true);
    let updated = null;
    if (editingNoteId) {
      updated = await dispatch(updateTaskNote(playerId, task._id, editingNoteId, text));
    } else {
      updated = await dispatch(addTaskNote(playerId, task._id, text));
    }
    if (updated) setTask(updated);
    setText('');
    setEditingNoteId(null);
    setSubmitting(false);
  };

  const handleDelete = async (noteId) => {
    const updated = await dispatch(deleteTaskNote(playerId, task._id, noteId));
    if (updated) setTask(updated);
  };

  const handleEdit = (note) => {
    setEditingNoteId(note._id);
    setText(note.text);
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setText('');
  };

  const canEdit = (note) => note.author === currentUserEmail;
  const canDelete = (note) => note.author === currentUserEmail || userRole === 'admin';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        zIndex: 1050,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: '10px',
          width: '100%',
          maxWidth: '560px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 18px',
            borderBottom: '1px solid #e2e8f0',
            background: '#fffbeb',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            <i className="fas fa-sticky-note" style={{ color: '#d97706', fontSize: '15px', flexShrink: 0 }} />
            <span
              style={{
                fontWeight: '600',
                fontSize: '14px',
                color: '#1e293b',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              title={task.label}
            >
              Notatki — {task.label}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '18px',
              color: '#64748b',
              cursor: 'pointer',
              padding: '0 4px',
              lineHeight: 1,
              flexShrink: 0,
            }}
            aria-label="Zamknij"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        {/* Info banner */}
        <div style={{ padding: '8px 18px', background: '#fef3c7', fontSize: '12px', color: '#92400e' }}>
          <i className="fas fa-eye-slash" style={{ marginRight: '6px' }} />
          Widoczne tylko dla admina i asystentek
        </div>

        {/* Notes list */}
        <div
          ref={notesContainerRef}
          onScroll={handleNotesScroll}
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '14px 18px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
          }}
        >
          {sortedNotes.length === 0 && (
            <p style={{ color: '#94a3b8', fontSize: '14px', textAlign: 'center', margin: 'auto' }}>
              Brak notatek. Dodaj pierwszą!
            </p>
          )}
          {sortedNotes.map(note => (
            <div
              key={note._id}
              style={{
                background: '#fffbeb',
                borderRadius: '8px',
                padding: '8px 12px',
                border: '1px solid #fde68a',
              }}
            >
              {/* Author + date */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginBottom: '4px',
                  flexWrap: 'wrap',
                }}
              >
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    color: note.authorRole === 'admin' ? '#1e40af' : '#7c3aed',
                    background: note.authorRole === 'admin' ? '#dbeafe' : '#f5f3ff',
                    borderRadius: '3px',
                    padding: '1px 6px',
                  }}
                >
                  {note.authorRole === 'admin' ? 'Admin' : 'Asystentka'}
                </span>
                <span style={{ fontSize: '11px', color: '#64748b' }}>
                  {note.author}
                </span>
                <span style={{ fontSize: '10px', color: '#94a3b8' }}>
                  · {formatDate(note.createdAt)}
                </span>
                {canEdit(note) && (
                  <button
                    type="button"
                    onClick={() => handleEdit(note)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#2e496b',
                      cursor: 'pointer',
                      fontSize: '11px',
                      padding: '0 2px',
                      marginLeft: '2px',
                    }}
                    title="Edytuj notatkę"
                  >
                    <i className="fas fa-pen" />
                  </button>
                )}
                {canDelete(note) && (
                  <button
                    type="button"
                    onClick={() => handleDelete(note._id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#e53e3e',
                      cursor: 'pointer',
                      fontSize: '11px',
                      padding: '0 2px',
                      marginLeft: '2px',
                    }}
                    title="Usuń notatkę"
                  >
                    <i className="fas fa-trash-alt" />
                  </button>
                )}
              </div>
              {/* Text */}
              {/* eslint-disable-next-line react/no-danger */}
              <div
                className="msg-bubble-content"
                style={{ fontSize: '13px', color: '#1e293b', wordBreak: 'break-word' }}
                dangerouslySetInnerHTML={{ __html: note.text }}
              />
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        {/* Input area */}
        <div
          style={{
            borderTop: '1px solid #e2e8f0',
            padding: '12px 18px',
            background: '#fffbeb',
          }}
        >
          <div className="thread-quill" style={{ marginBottom: '8px' }}>
            <ReactQuill
              value={text}
              onChange={setText}
              modules={quillModules}
              formats={quillFormats}
              theme="snow"
              placeholder="Napisz notatkę…"
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            {editingNoteId && (
              <button
                type="button"
                onClick={handleCancelEdit}
                style={{
                  background: '#f1f5f9',
                  color: '#475569',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 18px',
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                Anuluj
              </button>
            )}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isQuillEmpty(text) || submitting}
              style={{
                background: '#d97706',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 18px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: isQuillEmpty(text) || submitting ? 'not-allowed' : 'pointer',
                opacity: isQuillEmpty(text) || submitting ? 0.5 : 1,
              }}
            >
              <i className={`fas fa-${editingNoteId ? 'save' : 'plus'}`} style={{ marginRight: '6px' }} />
              {editingNoteId ? 'Zapisz' : 'Dodaj'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskNotesPanel;
